/**
 * TUVI - Comprobante de Ingreso
 * Genera el "COMPROBANTE DE INGRESO" (formato con encabezado/pie de Paz y Salvo).
 * Datos del cliente/proyecto se toman automáticos de la hoja de vida; las filas
 * (fecha, concepto, valor) se llenan manualmente. Exporta a PDF con html2pdf.
 */
import React, { useState, useRef } from 'react';
import html2pdf from 'html2pdf.js';
import { useNotification } from '../../context/NotificationContext';
import { ENCABEZADO_B64, PIE_B64 } from './comprobanteAssets';

const fmt = (v) => new Intl.NumberFormat('es-CO', {
  style: 'currency', currency: 'COP', minimumFractionDigits: 0, maximumFractionDigits: 0,
}).format(v || 0);

const CONCEPTOS_DEFAULT = ['Separación', 'Anticipo', 'Avance', 'Entrega Final'];

// Estilos del documento (inline para que html2canvas los lea bien)
const bd = '1px solid #000';
const cell = { border: bd, padding: '6px 8px', fontSize: '12px', verticalAlign: 'middle' };
const cellLabel = { ...cell, fontWeight: 'bold', background: '#f3f6ef' };
const cellHead = { ...cell, fontWeight: 'bold', textAlign: 'center', background: '#e8efe0' };

export default function ComprobanteIngresoModal({ hojaVida, onClose }) {
  const { error } = useNotification();
  const valorProyecto = Number(hojaVida?.valor_final || 0);
  const nombreCliente = hojaVida?.cliente_nombre || '';
  const proyectoApto = `${hojaVida?.proyecto_nombre || ''}${hojaVida?.apartamento ? ' - Apto ' + hojaVida.apartamento : ''}`.trim();

  const [rows, setRows] = useState(CONCEPTOS_DEFAULT.map(c => ({ fecha: '', concepto: c, valor: '' })));
  const [generando, setGenerando] = useState(false);
  const docRef = useRef(null);

  const totalPagos = rows.reduce((s, r) => s + (parseFloat(r.valor) || 0), 0);
  const saldo = valorProyecto - totalPagos;
  const pctPagado = valorProyecto > 0 ? (totalPagos / valorProyecto) * 100 : 0;
  const pctPendiente = 100 - pctPagado;

  const setRow = (i, k, v) => setRows(prev => prev.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const addRow = () => setRows(prev => [...prev, { fecha: '', concepto: '', valor: '' }]);
  const delRow = (i) => setRows(prev => prev.filter((_, j) => j !== i));

  const fmtFecha = (f) => {
    if (!f) return '';
    const d = new Date(f + 'T12:00:00');
    if (isNaN(d.getTime())) return f;
    return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
  };

  const filasDoc = rows.filter(r => (r.concepto || '').trim() || r.valor);

  const descargar = async () => {
    if (filasDoc.length === 0) { error('Agrega al menos un concepto con su valor'); return; }
    setGenerando(true);
    try {
      // Pequeña espera para asegurar el render del documento oculto
      await new Promise(r => setTimeout(r, 200));
      const nombreArchivo = `Comprobante_Ingreso_${(nombreCliente || 'cliente').replace(/\s+/g, '_')}.pdf`;
      await html2pdf().set({
        margin: [8, 8, 8, 8],
        filename: nombreArchivo,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, backgroundColor: '#ffffff', windowWidth: 780 },
        jsPDF: { unit: 'mm', format: 'letter', orientation: 'portrait' },
      }).from(docRef.current).save();
    } catch (e) {
      error('Error generando el PDF');
    } finally {
      setGenerando(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-comprobante ci-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Comprobante de Ingresos</h3>
          <button className="btn-cerrar" onClick={onClose}>X</button>
        </div>

        <div className="modal-body">
          <div className="ci-datos-fijos">
            <div><span>Cliente</span><b>{nombreCliente || '—'}</b></div>
            <div><span>Proyecto</span><b>{proyectoApto || '—'}</b></div>
            <div><span>Valor proyecto</span><b>{fmt(valorProyecto)}</b></div>
          </div>

          <p className="ci-ayuda">Llena las filas de pagos (fecha, concepto y valor). Los totales y porcentajes se calculan solos.</p>

          <table className="ci-edit-table">
            <thead>
              <tr><th style={{ width: 140 }}>Fecha</th><th>Concepto</th><th style={{ width: 150 }}>Valor pago</th><th style={{ width: 34 }}></th></tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td><input type="date" value={r.fecha} onChange={e => setRow(i, 'fecha', e.target.value)} /></td>
                  <td><input type="text" value={r.concepto} onChange={e => setRow(i, 'concepto', e.target.value)} placeholder="Concepto" /></td>
                  <td><input type="number" value={r.valor} onChange={e => setRow(i, 'valor', e.target.value)} placeholder="0" min="0" /></td>
                  <td><button className="ci-del" title="Quitar" onClick={() => delRow(i)}>✕</button></td>
                </tr>
              ))}
            </tbody>
          </table>
          <button className="ci-add" onClick={addRow}>+ Agregar fila</button>

          <div className="ci-totales">
            <div><span>Total pagos</span><b>{fmt(totalPagos)}</b></div>
            <div><span>Saldo pendiente</span><b className={saldo < 0 ? 'neg' : ''}>{fmt(saldo)}</b></div>
            <div><span>% Pagado</span><b>{pctPagado.toFixed(1)}%</b></div>
            <div><span>% Pendiente</span><b>{pctPendiente.toFixed(1)}%</b></div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn-cancelar" onClick={onClose}>Cancelar</button>
          <button className="btn-guardar" onClick={descargar} disabled={generando}>
            {generando ? 'Generando…' : '📄 Descargar PDF'}
          </button>
        </div>

        {/* ===== Documento oculto que se convierte a PDF ===== */}
        <div style={{ position: 'absolute', left: '-99999px', top: 0 }} aria-hidden="true">
          <div ref={docRef} style={{ width: '760px', background: '#fff', fontFamily: 'Arial, Helvetica, sans-serif', color: '#000' }}>
            <img src={ENCABEZADO_B64} alt="" style={{ width: '100%', display: 'block' }} />

            <div style={{ padding: '14px 22px 4px' }}>
              <div style={{ border: bd, textAlign: 'center', fontWeight: 'bold', fontSize: '15px', padding: '7px', letterSpacing: '1px' }}>
                COMPROBANTE DE INGRESO
              </div>

              {/* Datos del cliente / proyecto */}
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <colgroup>
                  <col style={{ width: '16%' }} /><col style={{ width: '34%' }} />
                  <col style={{ width: '25%' }} /><col style={{ width: '25%' }} />
                </colgroup>
                <tbody>
                  <tr>
                    <td style={cellLabel}>NOMBRE CLIENTE</td>
                    <td style={{ ...cell, textAlign: 'center' }} colSpan={3}>{nombreCliente}</td>
                  </tr>
                  <tr>
                    <td style={cellLabel}>PROYECTO</td>
                    <td style={{ ...cell, textAlign: 'center' }}>{proyectoApto}</td>
                    <td style={cellLabel}>VALOR PROYECTO</td>
                    <td style={{ ...cell, textAlign: 'center' }}>{fmt(valorProyecto)}</td>
                  </tr>
                </tbody>
              </table>

              {/* Tabla de pagos */}
              <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '10px' }}>
                <colgroup>
                  <col style={{ width: '16%' }} /><col style={{ width: '34%' }} />
                  <col style={{ width: '25%' }} /><col style={{ width: '25%' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th style={cellHead}>FECHA</th>
                    <th style={cellHead} colSpan={2}>CONCEPTO</th>
                    <th style={cellHead}>VALOR PAGO</th>
                  </tr>
                </thead>
                <tbody>
                  {filasDoc.map((r, i) => (
                    <tr key={i}>
                      <td style={{ ...cell, textAlign: 'center', color: '#b91c1c' }}>{fmtFecha(r.fecha)}</td>
                      <td style={{ ...cell, textAlign: 'center', fontWeight: 'bold' }} colSpan={2}>{r.concepto}</td>
                      <td style={{ ...cell, textAlign: 'center', color: '#1d4ed8', fontWeight: 'bold' }}>{fmt(parseFloat(r.valor) || 0)}</td>
                    </tr>
                  ))}
                  <tr>
                    <td style={cellLabel}>TOTAL PAGOS</td>
                    <td style={{ ...cell, textAlign: 'center', fontWeight: 'bold', color: '#15803d' }}>{fmt(totalPagos)}</td>
                    <td style={cellLabel}>SALDO PENDIENTE</td>
                    <td style={{ ...cell, textAlign: 'center', fontWeight: 'bold', color: '#b45309' }}>{fmt(saldo)}</td>
                  </tr>
                  <tr>
                    <td style={cellLabel}>% Pagado</td>
                    <td style={{ ...cell, textAlign: 'center' }}>{pctPagado.toFixed(1)}%</td>
                    <td style={cellLabel}>% Pendiente</td>
                    <td style={{ ...cell, textAlign: 'center' }}>{pctPendiente.toFixed(1)}%</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <img src={PIE_B64} alt="" style={{ width: '100%', display: 'block', marginTop: '24px' }} />
          </div>
        </div>
      </div>
    </div>
  );
}
